import { QuilttProvider } from '@quiltt/react';

import {
    InstitutionResolvabilityChecker,
    useCachedResolvabilityResults,
    useUnknownResolvability,
} from './quilttResolvability';

/**
 * Renders one error-tolerant {@link InstitutionResolvabilityChecker} per unique, not-yet-cached
 * institution, reporting into the connector-keyed cache. Errored or timed-out ones are re-checked once per mount. Pass `token` to wrap the checkers in a
 * QuilttProvider (onboarding warmers rendered outside the step); omit it when already inside one (the
 * step itself). Renders nothing when every institution already has a cached answer. `isEnabled` marks an
 * institution resolvable without a check (already-enabled ones).
 */
export default function InstitutionResolvabilityCheckers({
    institutions,
    connectorId,
    token,
    isEnabled,
}: Readonly<{
    institutions: Array<{ institutionId: string; institutionName: string }>;
    connectorId: string | undefined;
    token?: string;
    isEnabled?: (institutionId: string) => boolean;
}>) {
    const { results, handleResult } = useCachedResolvabilityResults(connectorId);
    const { unknownIds } = useUnknownResolvability(connectorId);

    const seen = new Set<string>();
    const unchecked = institutions.filter((institution) => {
        const hasAnswer =
            results[institution.institutionId] !== undefined && !unknownIds.has(institution.institutionId);
        if (hasAnswer || seen.has(institution.institutionId)) {
            return false;
        }
        seen.add(institution.institutionId);
        return true;
    });

    if (unchecked.length === 0) {
        return null;
    }

    const checkers = unchecked.map((institution) => (
        <InstitutionResolvabilityChecker
            key={institution.institutionId}
            institutionId={institution.institutionId}
            institutionName={institution.institutionName}
            connectorId={connectorId ?? ''}
            skipAsResolvable={isEnabled?.(institution.institutionId)}
            treatErrorAsUnsupported
            onResult={handleResult}
        />
    ));

    if (!token) {
        return checkers;
    }
    return <QuilttProvider token={token}>{checkers}</QuilttProvider>;
}
